import { type FC } from "react"
import styled from "styled-components"

import { toPriceString } from "@forento/core/utilities/currency"
import { toNumberFormat } from "@forento/core/utilities/number"

import Button from "~/components/Button"
import { HelpIcon } from "~/components/Icon"
import Layout, { PageHeader, PageTitle } from "~/components/Layout"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import { useUser } from "~/contexts/UserContext"
import AsideContent from "~/pages/main/DashboardPage/AsideContent"
import LatestPosts from "~/pages/main/DashboardPage/LatestPosts"
import Onboarding from "~/pages/main/DashboardPage/Onboarding"
import { cardColor } from "~/utilities/styles"
import { query } from "~/utilities/trpc"

import DashboardNotice from "./DashboardNotice"
import ForentoAiCard from "./ForentoAiCard"
import Guides from "./Guides"
import Milestones from "./Milestones"
import StatCard from "./StatCard"

const DashboardPage: FC = () => {
	const user = useUser().user!
	const platform = user.platform!
	// The quick-action card is a permanent fixture once AI is enabled, so it sits outside the one-at-a-time notices
	const isForentoAiCardVisible =
		platform.enabledFeatures.includes("ai-generated-courses") &&
		(platform.aiBetaCreditGrantDate === null || platform.aiBetaBannerDismissDate !== null)

	const { data, error } = query.dashboard.get.useQuery()
	// Milestones are optional, so a failed request hides the section instead of the dashboard
	const milestones = query.milestone.list.useQuery()

	const asideData = error
		? { status: "error" as const }
		: data === undefined
			? { status: "loading" as const }
			: {
					status: "success" as const,
					events: data.recentEvents,
				}

	return (
		<Layout asideContent={<AsideContent data={asideData} />}>
			<PageHeader>
				<PageTitle>Dashboard</PageTitle>
				<HelpCenter onClick="https://forento.link/dashboard-help-center" newTab>
					<HelpCenterIcon />
					Help Center
				</HelpCenter>
			</PageHeader>
			<Greeting>Welcome back, {user.firstName}</Greeting>
			{error ? (
				<p>Failed to load dashboard data.</p>
			) : data === undefined || milestones.isPending ? (
				<PartialLoadingPage />
			) : (
				<Content>
					<DashboardNotice
						publishedOfferCount={data.onboarding.publishedOfferCount}
						latestHighlight={data.creatorHighlights[0] ?? null}
					/>
					{isForentoAiCardVisible && <ForentoAiCard />}
					<div className="flex flex-wrap items-start gap-4">
						<div className="flex min-w-[min(100%,560px)] flex-[999_1_560px] flex-col gap-4 self-stretch">
							<Cards>
								<StatCard
									label="Sales last 30 days"
									value={toPriceString({ amount: data.sales.value, currency: data.sales.currency })}
									trend={data.sales.differenceFromLast}
								/>
								{data.visits && (
									<StatCard
										label="Visits last 30 days"
										value={toNumberFormat(data.visits.value)}
										trend={data.visits.differenceFromLast}
									/>
								)}
							</Cards>
							<Onboarding {...data.onboarding} />
						</div>
						<div className="flex min-w-[min(100%,280px)] flex-[1_1_280px] flex-col gap-4 self-stretch">
							<Guides />
						</div>
					</div>
					{milestones.data && <Milestones milestones={milestones.data} />}
					<LatestPosts productUpdates={data.productUpdates} creatorHighlights={data.creatorHighlights} />
					<AsideContent data={asideData} placement="inline" />
				</Content>
			)}
		</Layout>
	)
}

const HelpCenter = styled(Button)`
	color: #7d7d7d;
	font-size: 16px;
	font-weight: 600;
	line-height: 1;
	display: flex;
	align-items: center;
	gap: 8px;
`

const HelpCenterIcon = styled(HelpIcon)`
	width: 24px;
	height: 24px;
`

const Greeting = styled.p`
	font-size: 28px;
	font-weight: bold;
	margin-bottom: 16px;

	@media (max-width: 640px) {
		font-size: 24px;
	}
`

const Content = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`

const Cards = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 16px;

	& > * {
		flex: 1 1 220px;
		min-width: min(100%, 220px);
	}

	& > :nth-child(4n + 1) {
		background-color: ${cardColor.blue};
	}
	& > :nth-child(4n + 2) {
		background-color: ${cardColor.orange};
	}
	& > :nth-child(4n + 3) {
		background-color: ${cardColor.purple};
	}
	& > :nth-child(4n + 4) {
		background-color: ${cardColor.gray};
	}
`

export default DashboardPage
